import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Toast} from 'antd-mobile';
import styles from './index.module.scss';
import NFTHeader from '@/components/NFTHeader';
import ListView from '@/components/AntListView';
import ConfirmModal from '../components/ConfirmModal';
import {initCompose, getComposeMaterialList, synthCompose} from '@/api/projectManager_self';
import {hideLoading, showLoading} from '@/components/loading';
import {
  getActivityCodeFromQuery,
  getCollectionIdFromQuery,
  mapComposeInit,
  parseExternalQuery,
  useStatusBarInset,
  formatReservedCount,
  COMPOSE_MODE_RANDOM,
  COMPOSE_MODE_FIXED,
} from '../utils';
import {USE_MOCK, MOCK_INIT, getMockMaterialList, MOCK_SUCCESS} from '../mock';
import addIcon from '@/assets/img/collectionSynthesis/add@2x.png';

const fetchMaterialPage = async ({collectionId, currentPage, pageSize, useMock}) => {
  if (useMock) {
    return getMockMaterialList(collectionId, currentPage, pageSize);
  }
  const res = await getComposeMaterialList({
    currentPage,
    pageSize,
    collectionId: [Number(collectionId) || collectionId],
  });
  if (res.code !== '000000') throw res.msg;
  return {
    list: res.data?.list || [],
    totalCount: res.data?.totalCount,
  };
};

/**
 * 藏品合成（独立页，可供 App / 外部 WebView 按路由打开）
 * 路由：/collectionSynthesis 或 /collectionSynthesis/home
 * 入参：?cl=藏品ID&ac=活动码（可选 t=token）
 */
const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [slots, setSlots] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmOneClick, setConfirmOneClick] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [succData, setSuccData] = useState({});
  const [maxCount, setMaxCount] = useState('');
  const [limitType, setLimitType] = useState('');
  const [targetCollectionId, setTargetCollectionId] = useState('');
  const [materialMode, setMaterialMode] = useState('');
  const [randomCount, setRandomCount] = useState('');
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const submittingRef = useRef(false);
  const statusBarInset = useStatusBarInset();

  const query = useMemo(
    () => parseExternalQuery(location.search),
    [location.search]
  );
  const collectionId = getCollectionIdFromQuery(query) || location.state?.spHeChengInfo?.collectionId || '';
  const activityCode = getActivityCodeFromQuery(query) || location.state?.activityCode || '';

  const getSlotSelected = (slot) => {
    const val = selectedMap[slot?.slotKey];
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  };
  const selectedList = useMemo(
    () => slots.flatMap((slot) => getSlotSelected(slot)),
    [slots, selectedMap]
  );
  const confirmMaterials = useMemo(
    () => slots.map((slot) => {
      const list = getSlotSelected(slot);
      const preview = list[list.length - 1];
      return {
        slotKey: slot.slotKey,
        nftNo: preview?.nftNo,
        nftImageUrl: preview?.nftImageUrl || preview?.nftImage || preview?.imageBg || slot.imageBg,
        count: slot.requiredQuantity,
      };
    }).filter((item) => item.nftImageUrl),
    [slots, selectedMap]
  );
  const remainCount = maxCount === '' || maxCount == null ? null : Number(maxCount);
  const unlimitedCompose = Number(limitType) === 0;
  const remainValid = unlimitedCompose || remainCount == null || Number.isNaN(remainCount) || remainCount > 0;
  const isRandomMode = materialMode === COMPOSE_MODE_RANDOM;
  const isFixedMode = materialMode === COMPOSE_MODE_FIXED;
  const randomNeedCount = Number(randomCount);
  const randomNeedValid = !Number.isNaN(randomNeedCount) && randomNeedCount > 0;
  const allFilled = (() => {
    if (!slots.length) return false;
    if (isRandomMode) {
      return randomNeedValid ? selectedList.length === randomNeedCount : selectedList.length > 0;
    }
    if (isFixedMode) {
      const requiredSlots = slots.filter((slot) => Number(slot.requiredQuantity) > 0);
      if (!requiredSlots.length) return selectedList.length > 0;
      return requiredSlots.every((slot) => getSlotSelected(slot).length === Number(slot.requiredQuantity));
    }
    return selectedList.length > 0;
  })();
  const canManualCompose = allFilled && remainValid;
  const canOneClickCompose = remainValid;
  const fixedHintText = useMemo(() => {
    if (!isFixedMode) return '';
    const items = slots
      .map((slot, index) => Number(slot.requiredQuantity) > 0
        ? `材料${index + 1}:${formatReservedCount(slot.requiredQuantity)}个`
        : '')
      .filter(Boolean);
    if (items.length) return `合成需${items.join('，')}`;
    return `合成需${slots.map((slot, index) => `材料${index + 1}:${formatReservedCount(slot.requiredQuantity)}个`).join('，')}`;
  }, [isFixedMode, slots]);

  const showError = (error) => {
    Toast.show(typeof error === 'string' ? error : (error?.msg || '请求失败'));
  };

  const initPage = useCallback(async ({silent = false, resetTab = true} = {}) => {
    const fromState = location.state?.spHeChengInfo || {};
    let mapped = null;

    if (activityCode || collectionId) {
      try {
        if (!silent) showLoading();
        const params = {};
        if (activityCode) params.activityCode = activityCode;
        if (collectionId) params.collectionId = Number(collectionId) || collectionId;
        const res = await initCompose(params);
        if (!silent) hideLoading();
        if (res.code !== '000000') throw res.msg;
        mapped = mapComposeInit(res.data || {});
      } catch (error) {
        if (!silent) hideLoading();
        showError(error);
        return;
      }
    } else if (fromState.materialList || fromState.synthesizeInitVOS) {
      mapped = mapComposeInit(fromState);
    } else if (USE_MOCK) {
      mapped = mapComposeInit({
        ...MOCK_INIT,
        materialList: MOCK_INIT.synthesizeInitVOS,
        singleRemainCount: MOCK_INIT.maxCount,
        limitType: 1,
      });
    } else {
      Toast.show('缺少活动参数 ac 或藏品参数 cl');
      return;
    }

    setSlots(mapped.slots);
    setMaxCount(mapped.maxCount);
    setLimitType(mapped.limitType);
    setTargetCollectionId(mapped.collectionId || collectionId);
    setMaterialMode(mapped.materialMode);
    setRandomCount(mapped.randomCount);
    setSelectedMap({});
    if (resetTab) setActiveIndex(0);
  }, [activityCode, collectionId, location.state]);

  const refreshAfterCompose = useCallback(() => {
    setSelectedMap({});
    setListRefreshKey((key) => key + 1);
    initPage({silent: true, resetTab: false});
  }, [initPage]);

  useEffect(() => {
    initPage();
  }, [initPage]);

  const onSelectMaterial = (slot, item) => {
    const current = getSlotSelected(slot);
    if (current.some((selected) => String(selected?.nftNo) === String(item.nftNo))) {
      setSelectedMap((prev) => {
        const nextList = current.filter((selected) => String(selected?.nftNo) !== String(item.nftNo));
        const next = {...prev};
        if (nextList.length) {
          next[slot.slotKey] = nextList;
        } else {
          delete next[slot.slotKey];
        }
        return next;
      });
      return;
    }
    const occupied = Object.entries(selectedMap).some(([key, value]) => {
      if (key === slot.slotKey) return false;
      const list = Array.isArray(value) ? value : [value];
      return list.some((selected) => String(selected?.nftNo) === String(item.nftNo));
    });
    if (occupied) {
      Toast.show('该藏品已被选择');
      return;
    }
    if (isRandomMode && randomNeedValid && selectedList.length >= randomNeedCount) {
      Toast.show('已选满');
      return;
    }
    if (isFixedMode) {
      const required = Number(slot.requiredQuantity);
      if (required > 0 && current.length >= required) {
        Toast.show('该材料已选满');
        return;
      }
    }
    setSelectedMap((prev) => ({...prev, [slot.slotKey]: [...current, item]}));
  };

  const openConfirm = (oneClick = false) => {
    if (oneClick) {
      if (!canOneClickCompose) return;
      setConfirmOneClick(true);
      setConfirmVisible(true);
      return;
    }
    if (!canManualCompose) return;
    setConfirmOneClick(false);
    setConfirmVisible(true);
  };

  const onConfirmCompose = async () => {
    if (submittingRef.current) return;
    if (confirmOneClick) {
      if (!canOneClickCompose) return;
    } else if (!canManualCompose) {
      return;
    }
    if (!activityCode && USE_MOCK) {
      setSuccData(MOCK_SUCCESS);
      setConfirmVisible(false);
      setSuccessVisible(true);
      refreshAfterCompose();
      return;
    }
    const synthCollectionId = Number(collectionId || targetCollectionId) || collectionId || targetCollectionId;
    if (!activityCode || !synthCollectionId) {
      Toast.show('缺少活动参数 ac 或藏品参数 cl');
      return;
    }
    submittingRef.current = true;
    try {
      showLoading();
      const params = {
        activityCode,
        collectionId: synthCollectionId,
        num: 1,
        composeMode: confirmOneClick ? 1 : 0,
      };
      if (!confirmOneClick) {
        params.nftNo = selectedList.map((item) => item.nftNo).filter(Boolean);
      }
      const res = await synthCompose(params);
      hideLoading();
      if (res.code !== '000000') throw res.msg;
      const resultList = res.data?.list || [];
      const first = resultList[0] || {};
      setSuccData({
        ...first,
        nftImage: first.nftImage || first.nftImageUrl,
        nftNo: resultList.map((item) => item.nftNo).filter(Boolean).join(',') || first.nftNo,
        collectionName: first.collectionName,
      });
      setConfirmVisible(false);
      setSuccessVisible(true);
      refreshAfterCompose();
    } catch (error) {
      hideLoading();
      showError(error);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div
      className={styles.page}
      style={{'--sat': `${statusBarInset}px`}}
    >
      <NFTHeader
        midText="藏品合成"
        rightNode={() => <span className={styles.recordLink}>合成记录</span>}
        rightClick={() => navigate({
          pathname: '/collectionSynthesis/record',
          search: location.search,
        })}
        bgColor="#FFFFFF"
      />
      <div className={styles.body}>
        <div className={styles.topArea}>
        <p className={styles.hint}>
          当前最多可合成{unlimitedCompose ? '不限制' : (remainCount == null || Number.isNaN(remainCount) ? 'x' : remainCount)}个
        </p>
        <div className={styles.slots}>
          {slots.map((slot, index) => {
            const selected = getSlotSelected(slot);
            const preview = selected[selected.length - 1];
            const imgSrc = preview?.nftImageUrl || preview?.nftImage || preview?.imageBg || slot.imageBg || '';
            const selectedCount = selected.length;
            return (
              <div
                key={slot.slotKey}
                className={styles.slotWrap}
                onClick={() => setActiveIndex(index)}
              >
                <div className={styles.slot}>
                  {imgSrc ? <img src={imgSrc} alt="" /> : <div className={styles.slotPlaceholder} />}
                  {selectedCount > 0 ? (
                    <span className={styles.slotCount}>{selectedCount}</span>
                  ) : (
                    <div className={styles.slotMask}>
                      <img className={styles.plus} src={addIcon} alt="" />
                      <p>待添加</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={styles.btnRow}>
          <div
            className={`${styles.compoundBtn} ${canManualCompose ? styles.compoundBtnActive : ''}`}
            onClick={() => openConfirm(false)}
          >
            选择藏品去合成
          </div>
          <div
            className={`${styles.compoundBtn} ${canOneClickCompose ? styles.compoundBtnActive : ''}`}
            onClick={() => openConfirm(true)}
          >
            一键合成
          </div>
        </div>
        {isFixedMode && fixedHintText ? (
          <p className={styles.fixedHint}>{fixedHintText}</p>
        ) : null}
        </div>
        <div className={styles.panel}>
          <div className={styles.panelTitle}>请选择合成材料</div>
          {isRandomMode ? (
            <p className={styles.randomHint}>合成共需选择{formatReservedCount(randomCount)}个藏品</p>
          ) : null}
          <div className={styles.tabs}>
            {slots.map((slot, index) => (
              <div
                key={slot.slotKey}
                className={`${styles.tab} ${index === activeIndex ? styles.tabActive : ''}`}
                onClick={() => setActiveIndex(index)}
              >
                材料{index + 1}
              </div>
            ))}
          </div>
          <div className={styles.listWrap}>
            {slots.map((slot, index) => (
              index === activeIndex ? (
                <MaterialList
                  key={`${slot.slotKey}_${listRefreshKey}`}
                  collectionId={slot.collectionId}
                  useMock={USE_MOCK && !activityCode && !collectionId}
                  selectedNftNos={getSlotSelected(slot).map((item) => item.nftNo)}
                  onSelect={(item) => onSelectMaterial(slot, item)}
                />
              ) : null
            ))}
          </div>
        </div>
      </div>
      <ConfirmModal
        visible={confirmVisible}
        success={false}
        composeMode={materialMode}
        randomCount={randomCount}
        materials={confirmMaterials}
        onCancel={() => setConfirmVisible(false)}
        onConfirm={onConfirmCompose}
      />
      <ConfirmModal
        visible={successVisible}
        success
        result={succData}
        onConfirm={() => {
          setSuccessVisible(false);
        }}
      />
    </div>
  );
};

const MaterialList = ({collectionId, selectedNftNos = [], onSelect, useMock}) => {
  const [list, setList] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const currentPage = useRef(1);
  const pageSize = useRef(10);
  const loadingRef = useRef(false);

  const loadData = useCallback(async (isRefresh = true) => {
    if (!collectionId || loadingRef.current) return;
    loadingRef.current = true;
    try {
      if (isRefresh) {
        currentPage.current = 1;
      } else {
        currentPage.current += 1;
      }
      const {list: pageList, totalCount} = await fetchMaterialPage({
        collectionId,
        currentPage: currentPage.current,
        pageSize: pageSize.current,
        useMock,
      });
      setList((prev) => (isRefresh ? pageList : [...prev, ...pageList]));
      setHasMore(
        totalCount != null
          ? currentPage.current * pageSize.current < Number(totalCount)
          : pageList.length >= pageSize.current
      );
      setIsLoad(true);
    } catch (error) {
      Toast.show(typeof error === 'string' ? error : (error?.msg || '请求失败'));
      if (!isRefresh) currentPage.current = Math.max(1, currentPage.current - 1);
      setHasMore(false);
      setIsLoad(true);
    } finally {
      hideLoading();
      loadingRef.current = false;
    }
  }, [collectionId, useMock]);

  useEffect(() => {
    setList([]);
    setHasMore(false);
    setIsLoad(false);
    loadData(true);
  }, [loadData]);

  return (
    <div className={styles.list}>
      <ListView
        dataSource={list}
        hasMore={hasMore}
        isLoad={isLoad}
        emtyDesc="暂无材料"
        onEndReachedThreshold={80}
        renderItem={(item, index) => {
          const selected = selectedNftNos.some((nftNo) => String(nftNo) === String(item.nftNo));
          return (
            <div
              key={item.nftNo || item.id || index}
              className={`${styles.item} ${selected ? styles.itemSelected : ''}`}
              onClick={() => onSelect(item)}
            >
              <div className={styles.itemImg}>
                <img src={item.nftImageUrl || item.nftImage || item.imageBg} alt="" />
                <p>#{item.nftNo}</p>
              </div>
            </div>
          );
        }}
        onRefresh={async () => {
          await loadData(true);
        }}
        onEndReached={async () => {
          await loadData(false);
        }}
      />
    </div>
  );
};

export default Home;
